/**
 * HELP-HINT
 * Small "i" of a zone : opens the guide of the page on the section of the zone, and on its left a button that copies the text of the section
 * (with its formatting and its captures) to paste it in a mail, a document or a ticket
 * Displayed on hover of the zone (top right : same height as the icons of the cards) (the zone must be "group" and "relative", the Card is), only if the section is published
 */
import { useContext, useEffect, useRef, useState } from "react"
import { faCheck, faCircleInfo, faCopy } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import env from "@/env"
import Tooltip from "@/components/tooltip"
import { HelpGuideContext } from "@/components/help-guide.context"
import { hasHelpSection } from "@/utils/help-article.utils"
import { prepareHelpHtml } from "@/utils/help-render.utils"
import { getHelpAccountColors, getHelpValues } from "@/utils/use-help-render-context.utils"
import { selectSession } from "@/features/adm/entities/session/model/session.selector"
import { buildHelpClipboardHtml, htmlToPlainText, inlineHelpImages } from "@/utils/help-clipboard.utils"

interface OwnProps {
  section: string //Anchor of the section in the guide
  className?: string //Position (default : top right of the zone)
  isAlwaysVisible?: boolean //Not only on hover : for a page or a zone that is not a card
}

//Space taken on the left of the "i" by the copy button (icon + gap) : the icons that sit next to the hint make room for it
export const HELP_COPY_BUTTON_WIDTH: number = 28

function HelpHint(props: OwnProps) {
  const guide = useContext(HelpGuideContext)
  const session = useSelector(selectSession)
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
  }, [])

  if (!guide.isAvailable || !hasHelpSection(guide.article, props.section)) return null

  //Name of the card = title of its section in the guide
  const sectionTitle = guide.article?.sections.find((x) => x.anchor === props.section)?.title
  const copyLabel = sectionTitle ? `Copier la documentation de « ${sectionTitle} »` : "Copier la documentation de cette carte"

  async function copySection() {
    const section = guide.article?.sections.find((x) => x.anchor === props.section)
    if (!section) return

    //The values of the account are read when the button is used, not for every hint of the page
    const body = prepareHelpHtml(section.body, getHelpValues(session, guide.isGollac), getHelpAccountColors(session))
    const html = buildHelpClipboardHtml(section.title, body, env.REACT_APP_URL_STRAPI)
    const plain = new Blob([htmlToPlainText(html)], { type: "text/plain" })

    try {
      //The text is handed over as a promise : the images are read while the copy is still tied to the click
      const ClipboardItemConstructor = (window as any).ClipboardItem
      if (!ClipboardItemConstructor || !navigator.clipboard?.write) throw new Error("clipboard unavailable")
      await navigator.clipboard.write([
        new ClipboardItemConstructor({
          "text/html": inlineHelpImages(html).then((withImages) => new Blob([withImages], { type: "text/html" })),
          "text/plain": plain
        })
      ])
    } catch {
      //Browser without rich copy : the text alone
      try {
        await navigator.clipboard.writeText(await plain.text())
      } catch {
        toast.error("La copie n'a pas fonctionné")
        return
      }
    }

    setIsCopied(true)
    if (resetTimer.current) clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setIsCopied(false), 2000)
  }

  function onKey(event: React.KeyboardEvent, action: () => void) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.stopPropagation()
      action()
    }
  }

  return (
    <div
      className={
        "absolute z-10 flex items-center gap-[10px] " +
        (props.className ?? "top-[10px] right-3") +
        (props.isAlwaysVisible ? "" : " opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100")
      }>
      <Tooltip text={isCopied ? "Copié" : copyLabel} position="left">
        <div
          role="button"
          tabIndex={0}
          aria-label={copyLabel}
          className="cursor-pointer text-[#8C8C8C] hover:text-[#41475A]"
          onClick={(e) => {
            e.stopPropagation()
            e.currentTarget.blur() //A click must not keep the icons displayed (only the keyboard focus does)
            copySection()
          }}
          onKeyDown={(e) => onKey(e, copySection)}>
          <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} size="lg" />
        </div>
      </Tooltip>

      <Tooltip text="Guide utilisateur" position="left">
        <div
          role="button"
          tabIndex={0}
          aria-label="Ouvrir le guide utilisateur sur cette zone"
          className="cursor-pointer text-[#8C8C8C] hover:text-[#41475A]"
          onClick={(e) => {
            e.stopPropagation()
            e.currentTarget.blur()
            guide.open(props.section)
          }}
          onKeyDown={(e) => onKey(e, () => guide.open(props.section))}>
          <FontAwesomeIcon icon={faCircleInfo} size="lg" />
        </div>
      </Tooltip>
    </div>
  )
}

export default HelpHint
